import { HeaderNavigationModel } from '@/types';
import classNames from 'classnames';
import Link from '../general/Link';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { removeLinkQueryParams } from '@/utils/link-utils';
import Lock from '@/assets/lock.svg';

type DesktopNavAppModel = {
  links: HeaderNavigationModel[];
} & React.HTMLAttributes<HTMLDivElement>;

const DesktopNavApp = ({ links, className, ...props }: DesktopNavAppModel) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isOpen = openIndex !== null;
  const active = openIndex !== null ? links[openIndex] : null;

  const hasSubmenu = !!(
    active &&
    (active.props.column1NavigationItems?.length || 0) +
    (active.props.column2NavigationItems?.length || 0) +
    (active.props.column3NavigationItems?.length || 0) >
    0
  );

  return (
    <div
      className={classNames(className, 'relative flex h-full gap-1')}
      onMouseLeave={() => setOpenIndex(null)}
      {...props}
    >
      <nav className="flex h-full">
        {links.map((link, index) => (
          <DesktopNavItem key={index} link={link} isActive={openIndex === index} onOpen={() => setOpenIndex(index)} />
        ))}
      </nav>

      <div
        className={classNames(
          'fixed inset-x-0 top-[var(--header-height)] z-40 h-[calc(100vh-var(--header-height))] bg-black/30',
          'transition-all duration-200 ease-out',
          isOpen && hasSubmenu ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onMouseEnter={() => setOpenIndex(null)}
        //onClick={() => setOpenIndex(null)}
        aria-hidden
        id="menu-shadow"
      />

      <MegaMenuPanel
        key={openIndex!}
        link={active ?? ({} as any)}
        onMouseEnter={() => setOpenIndex(openIndex)}
        onMenuClick={() => setOpenIndex(null)}
        isOpen={isOpen && hasSubmenu}
      />
    </div>
  );
};

export default DesktopNavApp;

const DesktopNavItem = ({
  link,
  isActive,
  onOpen,
}: {
  link: HeaderNavigationModel;
  isActive: boolean;
  onOpen: () => void;
}) => {
  const base = classNames(
    'px-3 xl:px-7 inline-flex items-center h-full font-bold cursor-pointer whitespace-nowrap',
    'border-b transition-colors',
    isActive ? 'border-black' : 'border-transparent hover:border-black',
  );

  const label = link?.props?.label ?? '';

  return (
    <div className="relative h-full" onMouseEnter={onOpen} onFocus={onOpen} role="none">
      {link?.props?.link ? (
        <Link link={link.props.link} className={base}>
          {label}
        </Link>
      ) : (
        <div className={base}>{label}</div>
      )}
    </div>
  );
};

const MegaMenuPanel = ({
  link,
  isOpen,
  onMouseEnter,
  onMenuClick,
}: {
  link: HeaderNavigationModel;
  isOpen: boolean;
  onMouseEnter: () => void;
  onMenuClick: () => void;
}) => {
  const p = link?.props ?? ({} as any);
  const columns = [
    p.column1NavigationItems || [],
    p.column2NavigationItems || [],
    p.column3NavigationItems || [],
  ].filter((col) => col.length > 0);

  const { asPath } = useRouter();

  const currentPath = useMemo(() => {
    return removeLinkQueryParams(asPath);
  }, [asPath]);

  return (
    <div
      className={classNames(
        'bg-gray-2 fixed inset-x-0 top-[calc(var(--header-height)-1px)] z-50 max-h-[calc(100vh-var(--header-height))] overflow-auto shadow-md',
        'transition-all duration-200 ease-out will-change-transform',
        isOpen ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
      )}
      id="header-mega-menu"
      data-lenis-prevent
      onMouseEnter={onMouseEnter}
      aria-hidden={!isOpen}
    >
      <div className="container px-6 pt-6 pb-10">
        <div className="grid grid-cols-1 gap-5 divide-[#c4c4c4] md:grid-cols-3 md:divide-x">
          {columns?.length > 0 &&
            columns.map((col, i) => (
              <div className="flex flex-col gap-2" key={i}>
                {col.length > 0 &&
                  col.map((colItem, idx) => (
                    <div key={idx} className="mb-4 last:mb-0">
                      {col.length > 1 && (
                        <div className="text-md mb-3 font-bold text-gray-900 last:mb-0">{colItem.props.title}</div>
                      )}
                      {colItem.props.navigationItems.map((item, idx2) => (
                        <div key={idx2} className="text-base font-medium text-gray-800">
                          {item.link && (
                            <Link
                              onClick={onMenuClick}
                              link={item.link}
                              className={classNames(
                                'btn !leading-[18px] underline',
                                item.locked && 'opacity-50',
                                currentPath === item.link.href ? 'active !font-bold' : '!font-normal',
                              )}
                            >
                              {item.link.text} {item.locked && <Lock className="inline-block h-4 w-4" />}
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
